import { ClickAwayListener, Tooltip } from '@mui/material';
import {
  fetchVoucherData,
  resetVoucherData,
  updateVoucherStatus,
} from 'app/pages/TicketSalePage/slice';
import { selectVoucherData } from 'app/pages/TicketSalePage/slice/selectors';
import { Loader2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import { selectCartData } from 'store/cartSlice/selectors';
import { selectVendorFeatures } from 'store/vendorSlice/selectors';
import { getEventIdFromSlug } from 'utils/events';
import { OveitIcon } from '../OveitIcon';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const ApplyCode: React.FC<{}> = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [searchParams, setSearchParams] = useSearchParams();
  const voucherCodeFromURL = searchParams.get('code');

  const { eventSlug } = useParams();
  const eventId = getEventIdFromSlug(eventSlug);

  const [voucherCode, setVoucherCode] = useState('');
  const [isPrivateInvitation, setIsPrivateInvitation] = useState(false);
  const [tooltipIsOpen, setTooltipIsOpen] = useState(false);

  const {
    loading,
    status,
    voucherError,
    voucherData: { id: voucherId },
  } = useSelector(selectVoucherData);
  const {
    uuid,
    voucher: { voucherCode: cartVoucherCode },
    selectedEvent: { id: cartEventId },
  } = useSelector(selectCartData);

  const cartVoucher =
    cartVoucherCode && cartVoucherCode?.code ? cartVoucherCode?.code : '';

  const isValid = !!(status === 'valid' && (voucherId || isPrivateInvitation));

  const handleChange = e => {
    const newValue = e.target.value;
    setVoucherCode(newValue);
    if (newValue === '' && status === 'invalid') {
      dispatch(updateVoucherStatus({ status: 'unchecked' }));
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (isValid) return;
    getVoucherData(voucherCode);
  };

  const getVoucherData = async (voucherCode: string) => {
    const result = await dispatch(
      fetchVoucherData({
        eventId,
        voucherCode,
        uuid,
      }),
    );
    setIsPrivateInvitation(result.payload.private_tickets.length > 0);
  };

  const handleRemoveVoucher = () => {
    setVoucherCode('');
    handleRemoveVoucherFromURL();
    dispatch(resetVoucherData());
  };

  const handleRemoveVoucherFromURL = () => {
    if (searchParams.get('code')) {
      setSearchParams({});
    }
  };

  useEffect(() => {
    if (voucherCodeFromURL) {
      setVoucherCode(voucherCodeFromURL);
      getVoucherData(voucherCodeFromURL);
    }
  }, [voucherCodeFromURL]);

  useEffect(() => {
    if (cartEventId === eventId && cartVoucher) {
      setVoucherCode(cartVoucher);
      getVoucherData(cartVoucher);
    }
  }, [cartEventId, eventId, cartVoucher]);

  const handleCopyToClipboard = () => {
    let url = `${window.location.origin}${window.location.pathname}?code=${voucherCode}`;
    navigator.clipboard.writeText(url);
    setTooltipIsOpen(true);
  };

  const handleTooltipClose = () => {
    setTooltipIsOpen(false);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={`flex flex-col-reverse relative gap-1 ${
        voucherError && voucherError.length > 60 ? 'mb-[24px] sm:mb-0' : ''
      }`}
    >
      <Input
        value={voucherCode}
        onChange={handleChange}
        required
        type="text"
        placeholder={t('ticketSale.code') || ''}
        id="voucherCode"
        readOnly={isValid}
        className="read-only:bg-neutral-100"
      />

      {isValid ? (
        <Button
          onClick={handleRemoveVoucher}
          variant="ghost"
          className="absolute right-1 bottom-1"
          size="sm"
          type="button"
        >
          {t('ticketSale.remove')}
        </Button>
      ) : (
        <Button
          variant="ghost"
          className="absolute right-1 bottom-1"
          disabled={loading}
          size="sm"
        >
          {loading ? (
            <Loader2 className="animate-spin" />
          ) : (
            t('ticketSale.apply')
          )}
        </Button>
      )}
      <div className={`p3-semibold flex justify-between`}>
        <Label
          htmlFor="voucherCode"
          className="label-input p3-medium !flex gap-1 items-center"
        >
          {t('ticketSale.applyCode')}
          {isValid ? <OveitIcon name="solidGreenCheck" /> : null}
        </Label>
        {!loading && isValid ? (
          <ClickAwayListener onClickAway={handleTooltipClose}>
            <Tooltip
              title={t('common.copiedToClipboard')}
              placement="top"
              onClose={handleTooltipClose}
              open={tooltipIsOpen}
            >
              <p
                className="text-neutral-800 cursor-pointer underline"
                onClick={handleCopyToClipboard}
              >
                {t('ticketSale.shareLink')}
              </p>
            </Tooltip>
          </ClickAwayListener>
        ) : null}
      </div>
      {(status === 'invalid' || voucherError) && (
        <p className="standard-input-error absolute top-17">
          {voucherError || t('ticketSale.invalidCouponCode')}
        </p>
      )}
    </form>
  );
};

export default ApplyCode;
